import { getSupabaseClient } from '../../lib/supabase';
import type { Json } from '../../types/database';
import type {
  LocationOption,
  ProductDetail,
  ProductFilters,
  ProductHistoryItem,
  ProductListItem,
  ProductSearchSuggestion,
  ProductStorageCost,
  ProductStorageCostRange,
  ProductTag,
  SaveProductInput,
} from './types';
import { isExternalProductImage } from './image';

export { findProductIdByEan } from './barcodeApi';

const IMAGE_BUCKET = 'product-images';

// PostgREST accepts SQL NULL here, although generated RPC argument types cannot express it.
function sqlNullable<T>(value: T | null): T {
  return value as T;
}

export async function getProductImageUrls(
  paths: string[],
  thumbnail: boolean,
): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map();
  const uniquePaths = [...new Set(paths)];
  const urls = new Map<string, string>(
    uniquePaths.filter(isExternalProductImage).map((url) => [url, url.trim()]),
  );
  const storedPaths = uniquePaths.filter(
    (path) => !isExternalProductImage(path),
  );
  if (storedPaths.length === 0) return urls;
  const { data, error } = await getSupabaseClient()
    .storage.from(IMAGE_BUCKET)
    .createSignedUrls(storedPaths, 3600);
  if (error) return urls;
  data.forEach((item, index) => {
    if (!item.signedUrl) return;
    const signedUrl = thumbnail
      ? `${item.signedUrl.replace('/object/sign/', '/render/image/sign/')}&width=80&height=80&resize=cover`
      : item.signedUrl;
    urls.set(item.path ?? storedPaths[index], signedUrl);
  });
  return urls;
}

export async function listProducts(filters: ProductFilters) {
  const { data, error } = await getSupabaseClient().rpc(
    'fn_list_products_with_locations',
    {
      p_search: filters.search,
      p_tag_id: sqlNullable(filters.tagId),
      p_alert_only: filters.alertOnly,
      p_missing_dimensions_only: filters.missingDimensionsOnly,
      p_page: filters.page,
      p_page_size: filters.pageSize,
    },
  );
  if (error) throw new Error('Izdelkov ni bilo mogoče naložiti.');

  const imageUrls = await getProductImageUrls(
    data.flatMap((row) => (row.image_path ? [row.image_path] : [])),
    true,
  );
  const items: ProductListItem[] = data.map((row) => ({
    id: row.product_id,
    sku: row.sku,
    name: row.name,
    ean: row.ean,
    imagePath: row.image_path,
    imageUrl: row.image_path ? (imageUrls.get(row.image_path) ?? null) : null,
    primaryTag:
      row.primary_tag_id && row.primary_tag_name && row.primary_tag_color
        ? {
            id: row.primary_tag_id,
            name: row.primary_tag_name,
            color: row.primary_tag_color,
          }
        : null,
    totalQuantity: row.total_quantity,
    locationCount: row.location_count,
    locationCodes: row.location_codes,
    hasAlert: row.has_alert,
    missingDimensions: row.missing_dimensions,
    missingWeight: row.missing_weight,
    onTemporaryLocation: row.on_temporary_location,
    onMultipleMixedLocations: row.on_multiple_mixed_locations,
    matchScore: row.match_score,
    matchReason: row.match_reason,
  }));
  return { items, total: data[0]?.total_count ?? 0 };
}

export async function searchProductSuggestions(
  search: string,
  limit = 8,
): Promise<ProductSearchSuggestion[]> {
  if (!search.trim()) return [];
  const { data, error } = await getSupabaseClient().rpc('fn_list_products', {
    p_search: search,
    p_tag_id: sqlNullable<string>(null),
    p_alert_only: false,
    p_missing_dimensions_only: false,
    p_page: 1,
    p_page_size: limit,
  });
  if (error) throw new Error('Predlogov izdelkov ni bilo mogoče naložiti.');
  return data.map((row) => ({
    id: row.product_id,
    sku: row.sku,
    name: row.name,
    ean: row.ean,
    totalQuantity: row.total_quantity,
    matchScore: row.match_score ?? 0,
    matchReason: row.match_reason ?? 'Podoben zapis',
  }));
}

export async function listTags(): Promise<ProductTag[]> {
  const { data, error } = await getSupabaseClient()
    .from('tags')
    .select('id, name, color')
    .order('name');
  if (error) throw new Error('Tagov ni bilo mogoče naložiti.');
  return data.map((tag) => ({ ...tag }));
}

export async function listStockLocations(): Promise<LocationOption[]> {
  const { data, error } = await getSupabaseClient()
    .from('locations')
    .select(
      'id, code, display_name, kind, tracks_stock, position_number, level_number, warehouses(code), racks(code)',
    )
    .eq('is_active', true)
    .eq('tracks_stock', true)
    .order('code');
  if (error) throw new Error('Lokacij ni bilo mogoče naložiti.');
  return (
    data as unknown as Array<{
      id: string;
      code: string;
      display_name: string;
      kind: LocationOption['kind'];
      tracks_stock: boolean;
      position_number: number | null;
      level_number: number | null;
      warehouses: { code: string } | null;
      racks: { code: string } | null;
    }>
  ).map((location) => ({
    id: location.id,
    code: location.code,
    name: location.display_name,
    kind: location.kind,
    tracksStock: location.tracks_stock,
    warehouseCode: location.warehouses?.code ?? '',
    rackCode: location.racks?.code ?? null,
    positionNumber: location.position_number,
    levelNumber: location.level_number,
  }));
}

export async function getProduct(productId: string): Promise<ProductDetail> {
  const { data, error } = await getSupabaseClient().rpc(
    'fn_get_product_detail',
    { p_product_id: productId },
  );
  if (error || !data) throw new Error('Izdelka ni bilo mogoče naložiti.');
  const product = data as unknown as Omit<ProductDetail, 'imageUrl'>;
  const imageUrls = product.imagePath
    ? await getProductImageUrls([product.imagePath], false)
    : new Map<string, string>();
  return {
    ...product,
    imageUrl: product.imagePath
      ? (imageUrls.get(product.imagePath) ?? null)
      : null,
  };
}

export async function getProductHistory(
  productId: string,
  offset: number,
): Promise<{ items: ProductHistoryItem[]; total: number }> {
  const { data, error } = await getSupabaseClient().rpc(
    'fn_get_product_history',
    { p_product_id: productId, p_offset: offset, p_limit: 20 },
  );
  if (error) throw new Error('Zgodovine ni bilo mogoče naložiti.');
  return {
    items: data.map((row) => ({
      id: row.movement_id,
      type: row.movement_type,
      lines: row.lines as unknown as ProductHistoryItem['lines'],
      performedBy: row.performed_by,
      note: row.note,
      wholePallet: row.whole_pallet,
      createdAt: row.created_at,
    })),
    total: data[0]?.total_count ?? 0,
  };
}

export async function getProductStorageCost(
  productId: string,
  range: ProductStorageCostRange,
): Promise<ProductStorageCost> {
  const { data, error } = await getSupabaseClient().rpc(
    'fn_product_storage_cost',
    {
      p_product_id: productId,
      p_from_date: sqlNullable(range.fromDate),
      p_to_date: range.toDate,
    },
  );
  if (error) throw new Error('Stroška skladiščenja ni bilo mogoče izračunati.');
  if (!data) {
    throw new Error('Za tekoči mesec stroški skladišča še niso nastavljeni.');
  }
  return data as unknown as ProductStorageCost;
}

export async function saveProduct(input: SaveProductInput): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc('fn_save_product', {
    p_product_id: sqlNullable(input.productId),
    p_sku: input.sku,
    p_name: input.name,
    p_ean: input.ean,
    p_image_path: input.imagePath,
    p_notes: input.notes,
    p_length_cm: sqlNullable(input.lengthCm),
    p_width_cm: sqlNullable(input.widthCm),
    p_height_cm: sqlNullable(input.heightCm),
    p_weight_g: sqlNullable(input.weightG),
    p_tag_ids: input.tagIds,
    p_primary_tag_id: sqlNullable(input.primaryTagId),
    p_receipts: input.receipts.map((receipt) => ({
      location_id: receipt.locationId,
      quantity: receipt.quantity,
    })) as Json,
  });
  if (error || !data) {
    if (error?.code === '23505') throw new Error('SKU ali EAN že obstaja.');
    throw new Error(error?.message ?? 'Izdelka ni bilo mogoče shraniti.');
  }
  return data;
}

export async function uploadProductImage(image: Blob): Promise<string> {
  const path = `${crypto.randomUUID()}.webp`;
  const { error } = await getSupabaseClient()
    .storage.from(IMAGE_BUCKET)
    .upload(path, image, { contentType: 'image/webp', upsert: false });
  if (error) throw new Error('Slike ni bilo mogoče naložiti.');
  return path;
}

export async function cleanupProductImage(path: string): Promise<boolean> {
  const { error } = await getSupabaseClient()
    .storage.from(IMAGE_BUCKET)
    .remove([path]);
  return !error;
}

export async function saveTag(input: {
  id: string | null;
  name: string;
  color: string;
}): Promise<string> {
  const { data, error } = await getSupabaseClient().rpc('fn_save_tag', {
    p_tag_id: sqlNullable(input.id),
    p_name: input.name,
    p_color: input.color,
  });
  if (error || !data)
    throw new Error(error?.message ?? 'Taga ni bilo mogoče shraniti.');
  return data;
}

export async function deleteTag(tagId: string): Promise<void> {
  const { error } = await getSupabaseClient().rpc('fn_delete_tag', {
    p_tag_id: tagId,
  });
  if (error) throw new Error(error.message ?? 'Taga ni bilo mogoče izbrisati.');
}
